import { HamburgerIcon } from 'assets';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import BrandLogo from 'shared/components/brand_logo/BrandLogo';
import styles from './style.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import DropDownOptions from '../dropdown/dropdown_option/DropdownOption';
import { dropdownLogOutOptions } from './Constant';
import { resetUser } from 'shared/redux/reducers/UserSlice';
import { GetNotificationsQuery, MarkNotificationsReadMutation } from 'shared/services/webhook_service/WebhookService';
import moment from 'moment';
import { roleEnum } from 'shared/utils/Enum';
import ProfileEditModal from 'shared/modal/profile_edit/ProfileEditModal';

const topicLabels = {
    'orders/create': '🛒 New Order',
    'orders/updated': '✏️ Order Updated',
    'orders/cancelled': '❌ Order Cancelled',
    'orders/fulfilled': '✅ Order Fulfilled',
    'products/create': '📦 New Product',
    'products/update': '📦 Product Updated',
    'inventory_levels/update': '🏭 Inventory Updated',
    'customers/create': '👤 New Customer',
};

// ─── Order Detail Modal ──────────────────────────────────────────────────────
function NotifDetailModal({ notif, onClose, theme }) {
    if (!notif) return null;
    const payload = notif.payload || {};
    const lineItems = payload.line_items || [];
    const totalQty = lineItems.reduce((acc, item) => acc + (item.quantity || 0), 0);
    const totalPrice = payload.total_price || payload.total_price_set?.shop_money?.amount || '—';
    const currency = payload.currency || payload.presentment_currency || '';
    const orderNumber = payload.order_number || payload.name || payload.id || '—';
    const customerName = payload.customer
        ? `${payload.customer.first_name || ''} ${payload.customer.last_name || ''}`.trim()
        : null;

    const isDark = theme === 'Dark';
    const bg = isDark ? '#1a1a2e' : '#ffffff';
    const border = isDark ? '#2a2a40' : '#e4e4e7';
    const textPrimary = isDark ? '#f0f0f0' : '#111';
    const textSecondary = isDark ? '#8888aa' : '#888';
    const rowBg = isDark ? '#22223a' : '#f9f9f9';
    const headBg = isDark ? '#16162a' : '#f3f4f6';

    return (
        // Backdrop
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 999999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(2px)',
            }}
        >
            {/* Modal box */}
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: bg,
                    border: `1px solid ${border}`,
                    borderRadius: 20,
                    width: '92%',
                    maxWidth: 560,
                    maxHeight: '85vh',
                    overflowY: 'auto',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
                }}
            >
                {/* Header */}
                <div style={{
                    background: headBg,
                    borderRadius: '20px 20px 0 0',
                    padding: '18px 24px 14px',
                    borderBottom: `1px solid ${border}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <div>
                        <div style={{ fontSize: 11, color: textSecondary, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                            {topicLabels[notif.topic] || notif.topic}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: textPrimary, letterSpacing: '-0.5px' }}>
                            Order #{orderNumber}
                        </div>
                        {customerName && (
                            <div style={{ fontSize: 13, color: textSecondary, marginTop: 2 }}>👤 {customerName}</div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: isDark ? '#2a2a40' : '#f0f0f0',
                            border: 'none', borderRadius: '50%',
                            width: 32, height: 32, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, color: textSecondary, fontWeight: 700,
                        }}
                    >✕</button>
                </div>

                {/* Meta info row */}
                <div style={{
                    display: 'flex', gap: 12, padding: '14px 24px',
                    borderBottom: `1px solid ${border}`,
                    flexWrap: 'wrap',
                }}>
                    {[
                        { label: 'Shop', value: notif.shop },
                        { label: 'Received', value: moment(notif.received_at).format('DD MMM YYYY, HH:mm') },
                        payload.financial_status && { label: 'Payment', value: payload.financial_status },
                        payload.fulfillment_status && { label: 'Fulfillment', value: payload.fulfillment_status || 'Unfulfilled' },
                    ].filter(Boolean).map((item, i) => (
                        <div key={i} style={{
                            background: rowBg, borderRadius: 10,
                            padding: '6px 14px', fontSize: 12,
                        }}>
                            <span style={{ color: textSecondary, marginRight: 6 }}>{item.label}:</span>
                            <span style={{ color: textPrimary, fontWeight: 600 }}>{item.value}</span>
                        </div>
                    ))}
                </div>

                {/* Line Items */}
                <div style={{ padding: '16px 24px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        📋 Line Items
                        <span style={{ background: isDark ? '#2a2a40' : '#f0f0f0', color: textSecondary, borderRadius: 20, fontSize: 11, padding: '1px 8px', fontWeight: 600 }}>
                            {lineItems.length}
                        </span>
                    </div>

                    {lineItems.length === 0 ? (
                        <div style={{ color: textSecondary, fontSize: 13, padding: '12px 0' }}>No line items found.</div>
                    ) : (
                        <div style={{ borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
                            {/* Table head */}
                            <div style={{
                                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                                background: headBg, padding: '8px 14px',
                                fontSize: 11, fontWeight: 700, color: textSecondary,
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>
                                <span>Product / SKU</span>
                                <span>Country</span>
                                <span>Qty</span>
                                <span style={{ textAlign: 'right' }}>Unit Price</span>
                                <span style={{ textAlign: 'right' }}>Subtotal</span>
                            </div>

                            {/* Table rows */}
                            {lineItems.map((item, idx) => {
                                const unitPrice = parseFloat(item.price || 0);
                                const qty = item.quantity || 0;
                                const subtotal = (unitPrice * qty).toFixed(2);
                                const country =
                                    item.origin_location?.country_code ||
                                    item.origin_country_code ||
                                    payload.shipping_address?.country_code ||
                                    payload.billing_address?.country_code ||
                                    '—';
                                return (
                                    <div key={idx} style={{
                                        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                                        padding: '10px 14px',
                                        borderTop: idx === 0 ? 'none' : `1px solid ${border}`,
                                        background: idx % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
                                        alignItems: 'center',
                                    }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                                                {item.name || item.title || '—'}
                                            </div>
                                            {item.sku && (
                                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                                    SKU: <span style={{ fontWeight: 600 }}>{item.sku}</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* Country */}
                                        <div>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700,
                                                background: isDark ? '#2a2a40' : '#f0f0f0',
                                                color: textPrimary,
                                                borderRadius: 6, padding: '2px 8px',
                                                letterSpacing: 0.5,
                                            }}>
                                                {country}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>
                                            {qty}
                                        </div>
                                        <div style={{ textAlign: 'right', fontSize: 13, color: textPrimary }}>
                                            {currency} {unitPrice.toFixed(2)}
                                        </div>
                                        <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: textPrimary }}>
                                            {currency} {subtotal}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer totals */}
                <div style={{
                    borderTop: `1px solid ${border}`,
                    padding: '14px 24px 20px',
                    display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap',
                }}>
                    <div style={{ background: isDark ? '#22223a' : '#f3f4f6', borderRadius: 12, padding: '10px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Qty</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#6366f1', marginTop: 2 }}>{totalQty}</div>
                    </div>
                    <div style={{ background: isDark ? '#22223a' : '#f3f4f6', borderRadius: 12, padding: '10px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Price</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1a8a56', marginTop: 2 }}>{currency} {totalPrice}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function Navbar({ showSidebar, setShowSidebar, stickyNavbar }) {
    const { user } = useSelector((state) => state.root);
    const user_name = user?.user?.name;
    const dispatch = useDispatch();
    const { theme: { theme } } = useSelector((state) => state.root);

    const [options, setOption] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [selectedNotif, setSelectedNotif] = useState(null);
    const notifRef = useRef(null);

    const isAdmin = user?.user?.authorize === roleEnum.admin;
    const { data: notifData, refetch: refetchNotifs } = GetNotificationsQuery({ enabled: false, retry: false, refetchOnWindowFocus: false });
    const { mutate: markRead } = MarkNotificationsReadMutation();

    const notifications = notifData?.data || [];
    const unreadCount = notifData?.unread_count || 0;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleBellClick = () => {
        const nextState = !showNotifications;
        setShowNotifications(nextState);
        setOption(false);
        if (nextState) {
            refetchNotifs();
            if (unreadCount > 0) {
                markRead();
            }
        }
    };

    const logoutHandler = () => { 
        sessionStorage.clear();
        dispatch(resetUser()); 
    };

    const handleDropdownSelect = (itemTitle) => {
        if (itemTitle === 'Edit Profile') {
            setShowProfileModal(true);
        } else if (itemTitle === 'Logout') {
            logoutHandler();
        }
    };

    return (
        <div id="Navbar" className={classNames(`${theme}WhiteBg ${theme}BorderBottomColor`, styles.NavbarMainContainer, stickyNavbar && styles.StickyNavbar)}>
            <HamburgerIcon className={classNames(styles.HamIcon, `${theme}SvgIcon`)} onClick={() => setShowSidebar(!showSidebar)} />
            <BrandLogo brandStyle={styles.Brand} themeClass={theme} />
            <div className={classNames('ms-auto d-flex align-items-center gap-3', styles.NavbarBtnCont)}>

                {/* --- Notification Bell --- */}
                {isAdmin && (
                    <div ref={notifRef} style={{ position: 'relative' }}>
                        <button
                            onClick={handleBellClick}
                            style={{
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                position: 'relative', padding: '4px 6px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            title="Notifications"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme === 'Dark' ? '#fff' : '#333'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            </svg>
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute', top: 0, right: 0,
                                    background: '#e74c3c', color: '#fff', borderRadius: '100%',
                                    fontSize: '10px', fontWeight: 700, minWidth: '16px', height: '16px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '0 3px', lineHeight: 1,
                                }}>
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                        </button>

                        {/* Notification Dropdown */}
                        {showNotifications && (
                            <div style={{
                                position: 'fixed', top: '64px', right: '16px',
                                width: '360px', maxHeight: '480px', overflowY: 'auto',
                                background: theme === 'Dark' ? '#1a1a2e' : '#ffffff',
                                borderRadius: '16px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
                                zIndex: 99999, border: `1px solid ${theme === 'Dark' ? '#2a2a40' : '#e4e4e7'}`,
                            }}>
                                {/* Header */}
                                <div style={{
                                    padding: '16px 20px 12px',
                                    borderBottom: `1px solid ${theme === 'Dark' ? '#2a2a40' : '#f0f0f0'}`,
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: theme === 'Dark' ? '#16162a' : '#fafafa',
                                    borderRadius: '16px 16px 0 0',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme === 'Dark' ? '#a78bfa' : '#6366f1'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                        </svg>
                                        <span style={{ fontWeight: 700, fontSize: 15, color: theme === 'Dark' ? '#f0f0f0' : '#111', letterSpacing: '-0.2px' }}>
                                            Notifications
                                        </span>
                                    </div>
                                    {unreadCount > 0 && (
                                        <span style={{
                                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                            color: '#fff', borderRadius: '20px', fontSize: '11px',
                                            fontWeight: 700, padding: '2px 10px', letterSpacing: '0.3px',
                                        }}>
                                            {unreadCount} new
                                        </span>
                                    )}
                                </div>

                                {/* Notification List */}
                                {notifications.length === 0 ? (
                                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
                                        <div style={{ fontSize: 14, color: '#aaa', fontWeight: 500 }}>You're all caught up!</div>
                                        <div style={{ fontSize: 12, color: '#ccc', marginTop: 4 }}>No new notifications</div>
                                    </div>
                                ) : (
                                    notifications.map((notif, i) => (
                                        <div
                                            key={notif._id || i}
                                            onClick={() => { setSelectedNotif(notif); setShowNotifications(false); }}
                                            style={{
                                                padding: '14px 20px',
                                                borderBottom: `1px solid ${theme === 'Dark' ? '#22223a' : '#f3f3f3'}`,
                                                background: !notif.is_read
                                                    ? (theme === 'Dark' ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)')
                                                    : 'transparent',
                                                display: 'flex', gap: '12px', alignItems: 'flex-start',
                                                transition: 'background 0.15s',
                                                cursor: 'pointer',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = theme === 'Dark' ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.07)'}
                                            onMouseLeave={e => e.currentTarget.style.background = !notif.is_read ? (theme === 'Dark' ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)') : 'transparent'}
                                        >
                                            {/* Topic Icon */}
                                            <div style={{
                                                width: 38, height: 38, borderRadius: '10px',
                                                background: !notif.is_read
                                                    ? (theme === 'Dark' ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)')
                                                    : (theme === 'Dark' ? '#22223a' : '#f5f5f5'),
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 18, flexShrink: 0,
                                            }}>
                                                {(topicLabels[notif.topic] || '🔔').split(' ')[0]}
                                            </div>

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                                                    <div style={{
                                                        fontSize: 13, fontWeight: !notif.is_read ? 700 : 500,
                                                        color: theme === 'Dark' ? '#e8e8f0' : '#1a1a1a',
                                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                    }}>
                                                        {(topicLabels[notif.topic] || notif.topic).split(' ').slice(1).join(' ')}
                                                        {notif.payload?.order_number && (
                                                            <span style={{ color: '#6366f1', marginLeft: 4 }}>#{notif.payload.order_number}</span>
                                                        )}
                                                    </div>
                                                    {!notif.is_read && (
                                                        <div style={{ width: 8, height: 8, borderRadius: '100%', background: '#6366f1', flexShrink: 0 }} />
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 12, color: theme === 'Dark' ? '#7a7a9a' : '#888', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
                                                    {notif.shop}
                                                </div>
                                                <div style={{ fontSize: 11, color: theme === 'Dark' ? '#5a5a7a' : '#bbb', marginTop: 4, fontStyle: 'italic', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 4 }}>
                                                    {moment(notif.received_at).fromNow()}
                                                    <span style={{ color: '#6366f1', fontSize: 10, fontStyle: 'normal' }}>• tap to view</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* --- User Avatar --- */}
                <div className={classNames('d-flex', styles.AvatarContainer)} onClick={() => { setOption(!options); setShowNotifications(false); }}>
                    <div className={classNames(styles.Avatar)} style={{ padding: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', minWidth: '40px', minHeight: '40px', borderRadius: '50%' }}>
                        {user?.user?.profile_image || user?.user?.image_url ? (
                            <img
                                src={user?.user?.profile_image || user?.user?.image_url}
                                alt="profile"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            user_name?.slice(0, 2)
                        )}
                    </div>
                    <span className={classNames(styles.AvatarName, showSidebar && styles.ShowText)}>{user_name}</span>
                </div>
            </div>

            <DropDownOptions
                options={dropdownLogOutOptions}
                selectedItem={handleDropdownSelect}
                customContainer={styles.DropDown}
                openSelection={options}
                setOpenSelection={setOption}
            />

            {/* ─── Profile Edit Modal ─── */}
            <ProfileEditModal
                show={showProfileModal}
                handleClose={() => setShowProfileModal(false)}
            />

            {/* ─── Order Detail Modal ─── */}
            {selectedNotif && (
                <NotifDetailModal
                    notif={selectedNotif}
                    onClose={() => setSelectedNotif(null)}
                    theme={theme}
                />
            )}
        </div>
    );
}
